// The header beside tabs and search (feedback 01.11.2026): the user's tasks due today, the mails to assign, the
// customers' change requests (AB4) and a mail warning appear only when there is something to do; the bell holds what
// failed at Ange.Bot (operator 02.11.2026, chat-520); the user sits behind an avatar, with "Meine Aufgaben" in its
// menu; the full system status is in the settings.
import type { Alert, Meta } from "./api";
import { openInquiries, S } from "./state";
import { $, daysSince, esc, fmtDT } from "./util";

const letter = /\p{L}/u;

/** Two letters for the avatar: first and last name ("Pel, Alexander" as Entra may write it), else from the address. */
export function initials(name: string, email = ""): string {
  let n = name.replace(/\([^)]*\)/g, " ").trim();
  const comma = n.indexOf(",");
  if (comma > 0) n = `${n.slice(comma + 1)} ${n.slice(0, comma)}`;
  // Titles like "Dr." are no initial.
  let words = n.split(/\s+/).filter((w) => letter.test(w) && !w.endsWith("."));
  if (!words.length) words = (email.split("@")[0] ?? "").split(/[._+-]+/).filter((w) => letter.test(w));
  const letters = (w: string) => [...w].filter((c) => letter.test(c));
  const out = words.length > 1 ? letters(words[0]!)[0]! + letters(words.at(-1)!)[0]! : letters(words[0] ?? "").slice(0, 2).join("");
  return out.toLocaleUpperCase("de-DE") || "?";
}

/** The header pill: "1 Mail zuordnen", "3 Mails zuordnen"; none when every mail has its place. */
export const assignLabel = (n: number): string | null => (n > 0 ? `${n} ${n === 1 ? "Mail" : "Mails"} zuordnen` : null);

/** "1 Aufgabe heute", "3 Aufgaben heute": the user's tasks due today or overdue; none when there are none. */
export const taskLabel = (n: number): string | null => (n > 0 ? `${n} ${n === 1 ? "Aufgabe" : "Aufgaben"} heute` : null);

/** "1 Änderungsantrag", "2 Änderungsanträge" from the auftrag portal; none when nothing is open. */
export const changeRequestLabel = (n: number): string | null => (n > 0 ? `${n} ${n === 1 ? "Änderungsantrag" : "Änderungsanträge"}` : null);

export interface MailStatus {
  level: "ok" | "warn" | "crit";
  /** Short, for the header; names the problem. */
  text: string;
  /** The details, for the tooltip and the settings. */
  detail: string;
}

/**
 * The mail intake. Mails come in continuously, so long silence on a working day is suspicious (spec 13: alarm after
 * 4 h); failed jobs of the last 24 hours are an error. Null while nothing is loaded.
 */
export function mailStatus(m: Meta | null): MailStatus | null {
  if (!m) return null;
  if (!m.lastRunAt) return { level: "warn", text: "Noch keine Mail verarbeitet", detail: "Aus den Postfächern ist noch keine Mail verarbeitet worden." };
  const last = `Letzte Mail ${fmtDT(m.lastRunAt)}.`;
  if (m.lastRunStatus === "fehler") {
    const n = m.counts?.failedJobs ?? 0;
    const jobs = n === 1 ? "Ein Job ist" : `${n || "Mehrere"} Jobs sind`;
    return { level: "crit", text: "Fehler bei der Verarbeitung", detail: `${jobs} in den letzten 24 Stunden fehlgeschlagen. ${last}` };
  }
  if (daysSince(m.lastRunAt) > 1.5) return { level: "warn", text: `Keine Mail seit ${fmtDT(m.lastRunAt)}`, detail: `Seit über anderthalb Tagen ist keine Mail eingegangen. ${last}` };
  return { level: "ok", text: `Letzte Mail ${fmtDT(m.lastRunAt)}`, detail: last };
}

/** The bell's label: how many failures are new, or just its name. */
export const bellLabel = (n: number): string => (n > 0 ? `${n} ${n === 1 ? "neue Meldung" : "neue Meldungen"} von Ange.Bot` : "Meldungen");

/** One failure in the bell: what failed at which deal, when, who pressed, and why. */
export function alertItem(a: Alert): string {
  const what = a.kind === "push" ? (a.status === "rejected" ? "Push abgewiesen" : "Push fehlgeschlagen") : "Machbarkeitsprüfung fehlgeschlagen";
  return `<a class="menu-item alert${a.unread ? " unread" : ""}" href="#deal-${esc(a.dealId)}">
    <span class="al-h"><b>${what}</b><span class="mono">${fmtDT(a.doneAt ?? a.at)}</span></span>
    <span class="al-d"><span class="mono">${esc(a.ref)}</span> ${esc(a.deal)}${a.by ? ` · ${esc(a.by)}` : ""}</span>
    <span class="al-r">${esc(a.outcome)}</span></a>`;
}

/** Counts, the mails to assign and the mail warning; called with every render. */
export function renderHeader(): void {
  const inq = $("#inq-count");
  const n = openInquiries();
  inq.textContent = String(n);
  inq.classList.toggle("zero", n === 0);

  const tasks = $("#task-pill");
  const taskText = taskLabel(S.meta?.counts?.meineAufgaben ?? 0);
  tasks.hidden = !taskText;
  tasks.textContent = taskText ?? "";
  tasks.title = "Deine Aufgaben, heute fällig oder überfällig";

  const pill = $("#assign-pill");
  const label = assignLabel(S.unclear.length);
  pill.hidden = !label;
  pill.textContent = label ?? "";

  const cr = $("#cr-pill");
  const crLabel = changeRequestLabel(S.meta?.counts?.aenderungsantraege ?? 0);
  cr.hidden = !crLabel;
  cr.textContent = crLabel ?? "";
  cr.title = "Änderungsanträge von Kunden aus dem auftrag-Portal";

  const bell = $("#bell-btn");
  const news = S.meta?.counts?.meldungen ?? 0;
  const bellN = $("#bell-n");
  bellN.hidden = news === 0;
  bellN.textContent = String(news);
  bell.classList.toggle("on", news > 0);
  bell.title = bellLabel(news);
  bell.setAttribute("aria-label", bellLabel(news));

  const warn = $<HTMLAnchorElement>("#mailwarn");
  const st = mailStatus(S.meta);
  warn.hidden = !st || st.level === "ok";
  if (!st || st.level === "ok") return;
  warn.className = `hpill ${st.level}`;
  warn.title = st.detail;
  warn.innerHTML = `<span class="dot ${st.level}"></span>${esc(st.text)}`;
  // Admins find the mailboxes in the settings; for everyone else the tooltip says what is wrong.
  if (S.me?.isAdmin) warn.href = "#einstellungen";
  else warn.removeAttribute("href");
}

/** Avatar with the user's initials; the menu shows name, address and role and signs out. */
export function renderUser(): void {
  const me = S.me!;
  $("#user").hidden = false;
  $("#settings-btn").hidden = !me.isAdmin;
  const avatar = $("#avatar");
  avatar.textContent = initials(me.name, me.email);
  avatar.title = me.name;
  avatar.setAttribute("aria-label", `Konto von ${me.name}`);
  $("#user-menu").innerHTML = `<div class="menu-who"><b>${esc(me.name)}</b>${me.email ? `<span>${esc(me.email)}</span>` : ""}<span>Rolle: ${esc(me.roleLabel)}</span></div>
    <a class="menu-item" href="#aufgaben">Meine Aufgaben</a>
    <button type="button" class="menu-item" id="logout">Abmelden</button>`;
  $("#logout").onclick = () => void S.auth.logout();
}

/** Opens and closes the user menu: by click, closed again by Escape, an outside click or the focus leaving it. */
export function bindUserMenu(): void {
  const box = $("#user");
  const avatar = $("#avatar");
  const menu = $("#user-menu");
  const open = () => avatar.getAttribute("aria-expanded") === "true";
  const toggle = (on: boolean, refocus = false) => {
    menu.hidden = !on;
    avatar.setAttribute("aria-expanded", String(on));
    if (refocus) avatar.focus();
  };
  avatar.addEventListener("click", () => toggle(!open()));
  // A link in the menu leads elsewhere: the menu closes behind it.
  menu.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("a")) toggle(false);
  });
  box.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && open()) {
      e.stopPropagation();
      toggle(false, true);
    }
  });
  // Tab past the menu closes it; a click elsewhere too (it may not move the focus).
  box.addEventListener("focusout", (e) => {
    if (e.relatedTarget && !box.contains(e.relatedTarget as Node)) toggle(false);
  });
  document.addEventListener("click", (e) => {
    if (open() && !box.contains(e.target as Node)) toggle(false);
  });
}

/** The bell: the failures of the last 14 days and the way to the log; opening it counts them as seen. */
export function bindBell(): void {
  const box = $("#bell");
  const btn = $("#bell-btn");
  const panel = $("#bell-panel");
  const open = () => btn.getAttribute("aria-expanded") === "true";
  const toggle = (on: boolean, refocus = false) => {
    panel.hidden = !on;
    btn.setAttribute("aria-expanded", String(on));
    if (refocus) btn.focus();
    if (on) void fill();
  };
  const fill = async () => {
    panel.innerHTML = `<div class="menu-who">Lade …</div>`;
    const log = `<a class="menu-item" href="#protokoll">Protokoll aller Pushes und Prüfungen</a>`;
    try {
      const items = await S.api.alerts();
      panel.innerHTML = `<div class="menu-who"><b>Meldungen</b><span>Fehlgeschlagene Pushes und Machbarkeitsprüfungen der letzten 14 Tage</span></div>
        ${items.length ? items.map(alertItem).join("") : `<p class="al-none">Nichts schiefgegangen.</p>`}${log}`;
      if (items.some((a) => a.unread)) {
        await S.api.alertsSeen();
        if (S.meta) S.meta.counts.meldungen = 0;
        renderHeader();
      }
    } catch {
      panel.innerHTML = `<div class="menu-who">Die Meldungen konnten nicht geladen werden.</div>${log}`;
    }
  };
  btn.addEventListener("click", () => toggle(!open()));
  // A link leads elsewhere: the panel closes behind it.
  panel.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("a")) toggle(false);
  });
  box.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && open()) {
      e.stopPropagation();
      toggle(false, true);
    }
  });
  box.addEventListener("focusout", (e) => {
    if (e.relatedTarget && !box.contains(e.relatedTarget as Node)) toggle(false);
  });
  document.addEventListener("click", (e) => {
    if (open() && !box.contains(e.target as Node)) toggle(false);
  });
}
